import { Network, Station, Route, ProjectConfig, DEFAULT_CONFIG } from '../../types';
import { Project, PopulationSource } from '../../types';
import { URLManager } from '../url/URLManager';
import { ProjectStore } from '../storage/projects';

export interface GraphMLImportResult {
  network: Network;
  config?: ProjectConfig;
  projectName?: string;
  identifier?: string;
}

async function findNextAvailableNumber(baseIdentifier: string, store: ProjectStore): Promise<number> {
  const projects = await store.getAllProjects(false);
  const existingNumbers = projects
    .map(p => p.identifier)
    .filter(id => id.startsWith(baseIdentifier))
    .map(id => {
      const match = id.match(new RegExp(`${baseIdentifier}-(\\d+)$`));
      return match ? parseInt(match[1], 10) : 0;
    });
  
  if (existingNumbers.length === 0) return 1;
  return Math.max(...existingNumbers) + 1;
}

export async function parseGraphML(file: File): Promise<GraphMLImportResult> {
  const text = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/xml');
  const root = doc.documentElement;

  // Parse project metadata
  const projectName = getNodeData(root, 'projectName') || file.name.replace(/\.graphml$/, '');
  let identifier = getNodeData(root, 'identifier') || undefined;

  // Initialize ProjectStore to check identifier uniqueness
  const store = new ProjectStore();
  await store.init();

  // Check if the identifier is reserved or already exists
  if (identifier) {
    if (URLManager.getInstance().isReservedIdentifier(identifier)) {
      // Generate a new unique identifier with numeric suffix
      const nextNumber = await findNextAvailableNumber(identifier, store);
      identifier = `${identifier}-${nextNumber}`;
    } else {
      // Check if identifier already exists
      const isUnique = await store.isIdentifierUnique(identifier);
      if (!isUnique) {
        // Append number to make it unique
        const nextNumber = await findNextAvailableNumber(identifier, store);
        identifier = `${identifier}-${nextNumber}`;
      }
    }
  }

  // Parse network
  const network = parseNetwork(root);

  // Parse config if present
  const config = parseConfig(root);

  return {
    network,
    config,
    projectName,
    identifier
  };
}

function getNodeData(node: Element, key: string): string {
  return node.querySelector(`data[key="${key}"]`)?.textContent || '';
}

function parseNode(node: Element): Station {
  const id = node.getAttribute('id') || '';
  const name = getNodeData(node, 'name') || '';
  const number = parseInt(getNodeData(node, 'number') || '0');
  const population = parseInt(getNodeData(node, 'population') || '0');
  const populationSource = (getNodeData(node, 'population_source') as PopulationSource) || 'manual';
  const circlePopulation = parseInt(getNodeData(node, 'circle_population') || '0');
  const circlePopulationRadius = parseFloat(getNodeData(node, 'circle_population_radius') || '0');
  const latitude = parseFloat(getNodeData(node, 'latitude') || '0');
  const longitude = parseFloat(getNodeData(node, 'longitude') || '0');
  const positionX = parseFloat(getNodeData(node, 'positionX') || '0');
  const positionY = parseFloat(getNodeData(node, 'positionY') || '0');

  return {
    id,
    name,
    number,
    population,
    population_source: populationSource,
    circle_population: circlePopulation || null,
    circle_population_radius: circlePopulationRadius || null,
    latitude: latitude || null,
    longitude: longitude || null,
    position: { x: positionX, y: positionY }
  };
}

function parseNetwork(root: Element): Network {
  const network: Network = {
    stations: [],
    routes: []
  };

  const nodes = root.querySelectorAll('node');
  nodes.forEach(node => {
    const id = node.getAttribute('id');
    if (!id) return;

    const name = getNodeData(node, 'name') || '';
    const population = parseInt(getNodeData(node, 'population') || '0');
    const populationSource = (getNodeData(node, 'population_source') as PopulationSource) || 'manual';
    const circlePopulation = parseInt(getNodeData(node, 'circle_population') || '0');
    const circlePopulationRadius = parseFloat(getNodeData(node, 'circle_population_radius') || '0');
    const latitude = parseFloat(getNodeData(node, 'latitude') || '0');
    const longitude = parseFloat(getNodeData(node, 'longitude') || '0');
    const positionX = parseFloat(getNodeData(node, 'positionX') || '0');
    const positionY = parseFloat(getNodeData(node, 'positionY') || '0');

    network.stations.push({
      id,
      name,
      position: { x: positionX, y: positionY },
      number: parseInt(id),
      population,
      population_source: populationSource,
      circle_population: circlePopulation || null,
      circle_population_radius: circlePopulationRadius || null,
      latitude: latitude || null,
      longitude: longitude || null,
    });
  });

  const edges = root.querySelectorAll('edge');
  edges.forEach(edge => {
    const id = edge.getAttribute('id');
    const source = edge.getAttribute('source');
    const target = edge.getAttribute('target');
    if (!id || !source || !target) return;

    const distance = parseFloat(getNodeData(edge, 'distance') || '0');
    const landType = (getNodeData(edge, 'border') || 'domestic') as 'domestic' | 'customs' | 'no-customs';
    const distanceCalculation = (getNodeData(edge, 'distanceCalculation') || 'manual') as 'manual' | 'automatic';

    network.routes.push({
      id,
      source,
      target,
      distance,
      landType,
      distanceCalculation,
      automaticDistance: null // Will be calculated when needed
    });
  });

  return network;
}

function parseConfig(root: Element): ProjectConfig | undefined {
  const configData = root.querySelector('data[key="config"]')?.textContent;
  let config: ProjectConfig | undefined;
  if (configData) {
    try {
      const parsedConfig = JSON.parse(configData);
      
      // Normalize calculation values if they exist
      if (parsedConfig.calculation) {
        parsedConfig.calculation = {
          ...DEFAULT_CONFIG.calculation,
          ...parsedConfig.calculation,
          calibrationConstant: parseFloat(parsedConfig.calculation.calibrationConstant) || DEFAULT_CONFIG.calculation.calibrationConstant,
          calibrationConstantCustoms: parseFloat(parsedConfig.calculation.calibrationConstantCustoms) || DEFAULT_CONFIG.calculation.calibrationConstantCustoms,
          calibrationConstantNoCustoms: parseFloat(parsedConfig.calculation.calibrationConstantNoCustoms) || DEFAULT_CONFIG.calculation.calibrationConstantNoCustoms,
          densityFrictionFactor: Number((Math.max(0.10, Math.min(2.00, parseFloat(parsedConfig.calculation.densityFrictionFactor) || DEFAULT_CONFIG.calculation.densityFrictionFactor))).toFixed(2)),
          distanceFrictionFactor: parseFloat(parsedConfig.calculation.distanceFrictionFactor) || DEFAULT_CONFIG.calculation.distanceFrictionFactor,
          minimumRouteDistance: parseFloat(parsedConfig.calculation.minimumRouteDistance) || DEFAULT_CONFIG.calculation.minimumRouteDistance,
          populationRadius: Math.max(3, Math.min(50, parseFloat(parsedConfig.calculation.populationRadius) || DEFAULT_CONFIG.calculation.populationRadius))
        };
      }
      
      // Normalize cost values if they exist
      if (parsedConfig.costs) {
        parsedConfig.costs = {
          ...DEFAULT_CONFIG.costs,
          ...parsedConfig.costs,
          constructionCostPerKm: parseFloat(parsedConfig.costs.constructionCostPerKm) || 
                                parseFloat(parsedConfig.bond?.constructionCostPerKm) || 
                                DEFAULT_CONFIG.costs.constructionCostPerKm,
          farePerKm: parseFloat(parsedConfig.costs.farePerKm) || DEFAULT_CONFIG.costs.farePerKm,
          operatingCostPerKm: parseFloat(parsedConfig.costs.operatingCostPerKm) || DEFAULT_CONFIG.costs.operatingCostPerKm,
          businessShareOfSeats: parsedConfig.costs.businessShareOfSeats ? parseFloat(parsedConfig.costs.businessShareOfSeats) * 100 : DEFAULT_CONFIG.costs.businessShareOfSeats,
          businessTicketPremium: parsedConfig.costs.businessTicketPremium ? parseFloat(parsedConfig.costs.businessTicketPremium) * 100 : DEFAULT_CONFIG.costs.businessTicketPremium,
          discountShareOfSeats: parsedConfig.costs.discountShareOfSeats ? parseFloat(parsedConfig.costs.discountShareOfSeats) * 100 : DEFAULT_CONFIG.costs.discountShareOfSeats,
          discountTicketReduction: parsedConfig.costs.discountTicketReduction ? parseFloat(parsedConfig.costs.discountTicketReduction) * 100 : DEFAULT_CONFIG.costs.discountTicketReduction
        };
      } else if (parsedConfig.bond?.constructionCostPerKm) {
        // If costs section doesn't exist but constructionCostPerKm is in bond, create costs section
        parsedConfig.costs = {
          ...DEFAULT_CONFIG.costs,
          constructionCostPerKm: parseFloat(parsedConfig.bond.constructionCostPerKm) || DEFAULT_CONFIG.costs.constructionCostPerKm
        };
      }
      
      // Normalize train values if they exist
      if (parsedConfig.trains) {
        parsedConfig.trains = {
          ...DEFAULT_CONFIG.trains,
          ...parsedConfig.trains,
          seatsPerTrain: parseFloat(parsedConfig.trains.seatsPerTrain) || DEFAULT_CONFIG.trains.seatsPerTrain,
          loadFactor: parsedConfig.trains.loadFactor ? parseFloat(parsedConfig.trains.loadFactor) * 100 : DEFAULT_CONFIG.trains.loadFactor,
          maxSpeed: parseFloat(parsedConfig.trains.maxSpeed) || DEFAULT_CONFIG.trains.maxSpeed,
          minimumStationDwellTime: parseFloat(parsedConfig.trains.minimumStationDwellTime) || DEFAULT_CONFIG.trains.minimumStationDwellTime,
          maximumStationDwellTime: parseFloat(parsedConfig.trains.maximumStationDwellTime) || DEFAULT_CONFIG.trains.maximumStationDwellTime,
          minimumHeadway: Math.max(1, Math.min(30, parseFloat(parsedConfig.trains.minimumHeadway) || DEFAULT_CONFIG.trains.minimumHeadway))
        };
      }
      
      // Normalize bond values if they exist
      if (parsedConfig.bond) {
        parsedConfig.bond = {
          ...DEFAULT_CONFIG.bond,
          ...parsedConfig.bond,
          duration: parseFloat(parsedConfig.bond.duration) || DEFAULT_CONFIG.bond.duration,
          interestRate: parsedConfig.bond.interestRate ? parseFloat(parsedConfig.bond.interestRate) * 100 : DEFAULT_CONFIG.bond.interestRate,
          currency: parsedConfig.bond.currency || DEFAULT_CONFIG.bond.currency
        };
      }
      
      config = parsedConfig;
    } catch (error) {
      console.error('Failed to parse config:', error);
    }
  }

  return config;
} 